import { EventEmitter } from 'events';
import { debug } from '../Debug/debugDecorator';
import { sanitizeCoordinations } from '../Dimensions/coordinationsHelper';
import AppletVideoError from '../Error/AppletVideoError';
import ErrorCodes from '../Error/ErrorCodes';
import ErrorSuggestions from '../Error/ErrorSuggestions';
import SosError from '../Error/SosError';
import IPostMessage from '../IPostMessage';
import validate from '../Validate/Validate';
import type { IOptions } from './IOptions';
import IVideo from './IVideo';
import IVideoEvent, { getEventName, stringifySrcArguments, VideoEventType, VIDEO_EVENT_TYPES } from './IVideoEvent';
import IVideoMessage from './IVideoMessage';

const DEBUG_NAMESPACE = '@signageos/front-applet:FrontApplet:Video';

type IDeferred = {
	oncePromise: Promise<void>;
	reject?: (error: Error) => void;
};

/**
 * We know how crucial video is for digital signage. So we made it easy. This guide will walk you through playing video using the sos.video API. This allows you to play video natively on any platform.
 *
 * The `sos.video` provides you 4 methods to handle the playback:
 * - `prepare()` - Prepare will load the video into memory and prepare it for immediate playback.
 * - `play()` - This method loads the video into memory (if it hasn't been loaded previously) and starts the video playback.
 * - `pause()` / `resume()` - This method pauses the video that was previously started to play, it is possible to resume the playback with `resume()`.
 * - `stop()` - This method stops the playback and unloads the video from memory.
 *
 * :::warning Video identification
 * First 5 parameters (uri, x, y, width, and height) are unique identifiers for playing the video using play, stop, resume, and pause methods.
 * :::
 *
 * <details>
 *     <summary>Device Video Capabilities</summary>
 * 		| Capability | Description |
 * 		|:------------|:-------------|
 * 		| `VIDEO_4K` | If device supports playing 4k videos |
 *
 * 		If you want to check if the device supports this capability, use [`sos.display.supports()`](https://developers.signageos.io/sdk/sos/display#supports).
 * </details>
 *
 * ## Gapless video playback
 * It is often desirable to be able to play different videos consecutively without "black" frames. To achieve this next video in the queue needs to be loaded before the current video ends. How to achieve this may differ from platform to platform. Applet JS SDK handles this under the hood and provides you with `prepare()`, `play()` and `stop()` methods.
 * Each video has to be pre-loaded with `prepare()` while the previous video is still playing, and after the video ends it should be unloaded immediately with `stop()`. This is because some (especially older ones) platforms support that only 2 videos are loaded into memory at any time (one playing and one ready to play).
 *
 * ![](https://docs.signageos.io/hc/article_attachments/9093558407708)
 *
 * @example
 * const { filePath } = await sos.offline.cache.loadOrSaveFile(
 *  'video-1.mp4',
 *  'https://static.signageos.io/assets/video-test-1_e07fc21a7a72e3d33478243bd75d7743.mp4',
 * );
 *
 * const video = [filePath, 0, 0, 1280, 720];
 *
 * await sos.video.play(...video);
 *
 * // pause the video after 2s
 * setTimeout(() => sos.video.pause(...video), 2000);
 *
 * // resume the paused video
 * setTimeout(() => sos.video.resume(...video), 2500);
 *
 * // stop and unload the video
 * setTimeout(() => sos.video.stop(...video), 4000);
 */
export default class Video implements IVideo {
	public static MESSAGE_PREFIX: string = 'video';

	private eventEmitter: EventEmitter;
	private onceDeferredIndex: {
		[stringifiedSrcArguments: string]:
			| {
					ended: IDeferred;
					stop: IDeferred;
			  }
			| undefined;
	} = {};

	/** @internal */
	constructor(
		private messagePrefix: string,
		private postMessage: IPostMessage<void>,
	) {
		this.eventEmitter = new EventEmitter();
	}

	/**
	 * The `play()` method starts the playback of a video or video that has been previously prepared by the `prepare()` method.
	 *
	 * @param uri Address to remote (online) or local video file
	 * @param x x-position for video on screen
	 * @param y y-position for video on screen
	 * @param width Video width on screen
	 * @param height Video height on screen
	 * @throws Error If the parameters are not valid.
	 * @throws Error If any error occurs during the playback of the video.
	 * @returns {Promise<void>} Returns a promise that resolves when the video starts playing.
	 * @since 1.0.3
	 *
	 * @example // {@link https://github.com/signageos/applet-examples/blob/master/examples/content-js-api/video-loop-one | Play one video in infinite loop}
	 * @example // {@link https://github.com/signageos/applet-examples/tree/master/examples/content-js-api/video-multiple | Play multiple videos in infinite loop}
	 * @example // {@link https://github.com/signageos/applet-examples/tree/master/examples/content-js-api/video-and-images | Switching video and images in infinite loop}
	 *
	 * @example
	 * const { filePath } = await sos.offline.cache.loadOrSaveFile('fileName', 'https://example.com/video.mp4');
	 * const video = [filePath, 0, 0, 1920, 1080];
	 * // Play the video
	 * await sos.video.play(...video);
	 */
	@debug(DEBUG_NAMESPACE)
	public async play(uri: string, x: number, y: number, width: number, height: number): Promise<void> {
		this.checkParamsValidity(uri, x, y, width, height);
		({ x, y, width, height } = sanitizeCoordinations({ x, y, width, height }));
		this.removeAllVideoListeners(uri, x, y, width, height); // If not explicitly stopped and played again the same video
		const onceEndedDeferred = this.createOncePromise('ended', uri, x, y, width, height);
		const onceStopDeferred = this.createOncePromise('stop', uri, x, y, width, height);
		await this.postMessage({
			type: this.getMessage('play'),
			uri,
			x,
			y,
			width,
			height,
		});

		const srcArgumentsString = stringifySrcArguments(uri, x, y, width, height);
		this.onceDeferredIndex[srcArgumentsString] = {
			ended: onceEndedDeferred,
			stop: onceStopDeferred,
		};
		this.createAndEmitEvent('play', uri, x, y, width, height);
	}

	/**
	 * The `prepare()` method loads the video into memory and prepares it for a playback, this is useful for gapless playback.
	 *
	 * @param uri Address to remote (online) or local video file
	 * @param x x-position for video on screen
	 * @param y y-position for video on screen
	 * @param width Video width on screen
	 * @param height Video height on screen
	 * @param options Optional options for preparing the video.
	 * @param options.background If view should be prepared in background.
	 * @param options.volume Initial volume value of the video.
	 * @throws Error If the parameters are not valid.
	 * @throws Error If any error occurs during the preparation of the video.
	 * @returns {Promise<void>} Returns a promise which resolves when the video is prepared.
	 * @since 1.0.3
	 *
	 * @example
	 * // Example of preparing and playing a video
	 * const { filePath } = await sos.offline.cache.loadOrSaveFile('video-1.mp4', 'https://static.signageos.io/assets/video-test-1_e07fc21a7a72e3d33478243bd75d7743.mp4');
	 * const video = [filePath, 0, 0, 1920, 1080];
	 * // Prepare the video
	 * await sos.video.prepare(...video);
	 * // Play the video
	 * await sos.video.play(...video);
	 */
	@debug(DEBUG_NAMESPACE)
	public async prepare(uri: string, x: number, y: number, width: number, height: number, options: IOptions = {}): Promise<void> {
		this.checkParamsValidity(uri, x, y, width, height);
		({ x, y, width, height } = sanitizeCoordinations({ x, y, width, height }));
		await this.postMessage({
			type: this.getMessage('prepare'),
			uri,
			x,
			y,
			width,
			height,
			options,
		});
		this.createAndEmitEvent('prepare', uri, x, y, width, height);
	}

	/**
	 * The `stop()` method stops the video; it can't be resumed with the `resume()` method.
	 *
	 * @param uri Address to remote (online) or local video file
	 * @param x x-position for video on screen
	 * @param y y-position for video on screen
	 * @param width Video width on screen
	 * @param height Video height on screen
	 * @returns {Promise<void>} Returns a promise which resolves when the video is stopped.
	 * @throws Error If the parameters are not valid.
	 * @throws Error If any error occurs during the stopping of the video.
	 * @since 1.0.3
	 *
	 * @example
	 * // Example of stopping a video
	 * const { filePath } = await sos.offline.cache.loadOrSaveFile('video-1.mp4', 'https://static.signageos.io/assets/video-test-1_e07fc21a7a72e3d33478243bd75d7743.mp4');
	 * const video = [filePath, 0, 0, 1920, 1080];
	 * // Prepare and play the video
	 * await sos.video.prepare(...video);
	 * await sos.video.play(...video);
	 * // Stop the video after 5 seconds
	 * setTimeout(() => sos.video.stop(...video), 5000);
	 */
	@debug(DEBUG_NAMESPACE)
	public async stop(uri: string, x: number, y: number, width: number, height: number): Promise<void> {
		this.checkParamsValidity(uri, x, y, width, height);
		({ x, y, width, height } = sanitizeCoordinations({ x, y, width, height }));
		await this.postMessage({
			type: this.getMessage('stop'),
			uri,
			x,
			y,
			width,
			height,
		});
		this.createAndEmitEvent('stop', uri, x, y, width, height);
		const srcArgumentsString = stringifySrcArguments(uri, x, y, width, height);
		this.onceDeferredIndex[srcArgumentsString]?.ended.reject?.(
			new AppletVideoError({
				kind: 'appletVideoError',
				message: `This video was stopped already. The arguments: ${srcArgumentsString}`,
				code: ErrorCodes.VIDEO_WAS_STOPPED_ALREADY,
				suggestion: ErrorSuggestions.VIDEO_WAS_STOPPED_ALREADY,
			}),
		);
		delete this.onceDeferredIndex[srcArgumentsString];
	}

	/**
	 * The `pause()` method pauses the video; playback can be resumed with `resume()`. Stopped video can't be paused.
	 *
	 * @param uri Address to remote (online) or local video file
	 * @param x x-position for video on screen
	 * @param y y-position for video on screen
	 * @param width Video width on screen
	 * @param height Video height on screen
	 * @returns {Promise<void>} Returns a promise that resolves when the video is paused.
	 * @throws Error If the parameters are not valid.
	 * @throws Error If any error occurs during the pausing of the video.
	 * @since 1.0.3
	 *
	 * @example
	 * // Example of resuming a paused video
	 * const { filePath } = await sos.offline.cache.loadOrSaveFile('video-1.mp4', 'https://static.signageos.io/assets/video-test-1_e07fc21a7a72e3d33478243bd75d7743.mp4');
	 * const video = [filePath, 0, 0, 1920, 1080];
	 * // Prepare the video
	 * await sos.video.prepare(...video);
	 * // Play the video
	 * await sos.video.play(...video);
	 * // Pause the video after 2 seconds
	 * setTimeout(() => sos.video.pause(...video), 2000);
	 */
	@debug(DEBUG_NAMESPACE)
	public async pause(uri: string, x: number, y: number, width: number, height: number): Promise<void> {
		this.checkParamsValidity(uri, x, y, width, height);
		({ x, y, width, height } = sanitizeCoordinations({ x, y, width, height }));
		await this.postMessage({
			type: this.getMessage('pause'),
			uri,
			x,
			y,
			width,
			height,
		});
		this.createAndEmitEvent('pause', uri, x, y, width, height);
	}

	/**
	 * The `resume()` method resumes the video paused by `pause()` function. Stopped video can't be resumed.
	 *
	 * @param uri Address to remote (online) or local video file
	 * @param x x-position for video on screen
	 * @param y y-position for video on screen
	 * @param width Video width on screen
	 * @param height Video height on screen
	 * @returns {Promise<void>} Returns a promise that resolves when the video is resumed.
	 * @throws Error If the parameters are not valid.
	 * @throws Error If any error occurs during the resuming of the video.
	 * @since 1.0.3
	 *
	 * @example
	 * // Example of resuming a paused video
	 * const { filePath } = await sos.offline.cache.loadOrSaveFile('video-1.mp4', 'https://static.signageos.io/assets/video-test-1_e07fc21a7a72e3d33478243bd75d7743.mp4');
	 * const video = [filePath, 0, 0, 1920, 1080];
	 * // Prepare the video
	 * await sos.video.prepare(...video);
	 * // Play the video
	 * await sos.video.play(...video);
	 * // Pause the video after 2 seconds
	 * setTimeout(() => sos.video.pause(...video), 2000);
	 * // Resume the video after 3 seconds
	 * setTimeout(() => sos.video.resume(...video), 5000);
	 */
	@debug(DEBUG_NAMESPACE)
	public async resume(uri: string, x: number, y: number, width: number, height: number): Promise<void> {
		this.checkParamsValidity(uri, x, y, width, height);
		({ x, y, width, height } = sanitizeCoordinations({ x, y, width, height }));
		await this.postMessage({
			type: this.getMessage('resume'),
			uri,
			x,
			y,
			width,
			height,
		});
		this.createAndEmitEvent('resume', uri, x, y, width, height);
	}
	/**
	 * The `onceEnded()` method returns a promise that is resolved after the specified video ends.
	 *
	 * @param uri Address to remote (online) or local video file
	 * @param x x-position for video on screen
	 * @param y y-position for video on screen
	 * @param width Video width on screen
	 * @param height Video height on screen
	 * @returns {Promise<void>} Returns a promise that resolves when the video ends.
	 * @throws Error If the parameters are not valid.
	 * @throws Error If the video was not played yet.
	 * @since 1.0.29
	 *
	 * @example
	 * // Example of using onceEnded to wait for a video to end
	 * await sos.video.prepare('https://example.com/video.mp4', 0, 0, 1920, 1080);
	 * while (true) {
	 *   await sos.video.play('https://example.com/video.mp4', 0, 0, 1920, 1080);
	 *   // Wait for the video to stop to start playing it again
	 *   await sos.video.onceEnded('https://example.com/video.mp4', 0, 0, 1920, 1080);
	 * }
	 */
	@debug(DEBUG_NAMESPACE)
	public onceEnded(uri: string, x: number, y: number, width: number, height: number): Promise<void> {
		this.checkParamsValidity(uri, x, y, width, height);
		({ x, y, width, height } = sanitizeCoordinations({ x, y, width, height }));
		const srcArgumentsString = stringifySrcArguments(uri, x, y, width, height);
		const onceDeferreds = this.onceDeferredIndex[srcArgumentsString];
		if (typeof onceDeferreds === 'undefined') {
			throw new AppletVideoError({
				kind: 'appletVideoError',
				message: `This video was not played yet. The arguments: ${srcArgumentsString}`,
				code: ErrorCodes.VIDEO_WAS_NOT_PLAYED_YET,
				suggestion: ErrorSuggestions.VIDEO_WAS_NOT_PLAYED,
			});
		}
		return onceDeferreds.ended.oncePromise;
	}

	/**
	 * The `onceStop()` method returns a promise that is resolved after the specified video has been stopped.
	 *
	 * @param uri Address to remote (online) or local video file
	 * @param x x-position for video on screen
	 * @param y y-position for video on screen
	 * @param width Video width on screen
	 * @param height Video height on screen
	 * @returns {Promise<void>} Returns a promise that resolves when the video is stopped.
	 * @throws Error If the parameters are not valid.
	 * @throws Error If the video was not played yet.
	 * @since 1.0.29
	 *
	 * @example
	 * // Example of using onceStop to wait for a video to stop
	 * await sos.video.prepare('https://example.com/video.mp4', 0, 0, 1920, 1080);
	 * while (true) {
	 *   await sos.video.play('https://example.com/video.mp4', 0, 0, 1920, 1080);
	 *   // Wait few seconds for the video to play
	 *   await new Promise((resolve) => setTimeout(resolve, 5000));
	 *   // Stop the video and wait for it to stop
	 *   await sos.video.stop('https://example.com/video.mp4', 0, 0, 1920, 1080);
	 *   await sos.video.onceStop('https://example.com/video.mp4', 0, 0, 1920, 1080);
	 *   // Now the video is stopped, and we can play it again
	 * }
	 */
	@debug(DEBUG_NAMESPACE)
	public onceStop(uri: string, x: number, y: number, width: number, height: number): Promise<void> {
		this.checkParamsValidity(uri, x, y, width, height);
		({ x, y, width, height } = sanitizeCoordinations({ x, y, width, height }));
		const srcArgumentsString = stringifySrcArguments(uri, x, y, width, height);
		const onceDeferreds = this.onceDeferredIndex[srcArgumentsString];
		if (typeof onceDeferreds === 'undefined') {
			throw new AppletVideoError({
				kind: 'appletVideoError',
				message: `This video was not played yet. The arguments: ${srcArgumentsString}`,
				code: ErrorCodes.VIDEO_WAS_NOT_PLAYED_YET,
				suggestion: ErrorSuggestions.VIDEO_WAS_NOT_PLAYED,
			});
		}
		return onceDeferreds.stop.oncePromise;
	}

	/**
	 * The `onPlay()` method sets up a listener, which is called whenever a video starts to play.
	 *
	 * @param listener The listener function to be called when a play event occurs.
	 * @returns {void} Resolves when the listener is successfully set up.
	 * @since 1.0.3
	 *
	 * @example
	 * // Play a video and handle the play event
	 * await sos.video.prepare('https://example.com/video.mp4', 0, 0, 1920, 1080);
	 * sos.video.onPlay((event) => {
	 *     console.log('Video started playing:', event);
	 * });
	 * await sos.video.play('https://example.com/video.mp4', 0, 0, 1920, 1080);
	 */
	@debug(DEBUG_NAMESPACE)
	public onPlay(listener: (event: IVideoEvent) => void): void {
		validate({ listener }).required().function();
		this.eventEmitter.addListener('play', listener);
	}

	/**
	 * The `onPrepare()` method sets up a listener, which is called whenever a video starts to be prepared.
	 *
	 * @param listener The listener function to be called when a prepare event occurs.
	 * @returns {void} Resolves when the listener is successfully set up.
	 * @since 1.0.3
	 *
	 * @example
	 * // Play a video and handle the prepare event
	 * await sos.video.prepare('https://example.com/video.mp4', 0, 0, 1920, 1080);
	 * sos.video.onPrepare((event) => {
	 *     console.log('Video prepared:', event);
	 * });
	 */
	@debug(DEBUG_NAMESPACE)
	public onPrepare(listener: (event: IVideoEvent) => void): void {
		validate({ listener }).required().function();
		this.eventEmitter.addListener('prepare', listener);
	}

	/**
	 * The `onStop()` method sets up a listener, which is called whenever a video has been stopped with `stop()` method.
	 *
	 * @param listener The listener function to be called when a stop event occurs.
	 * @returns {void} Resolves when the listener is successfully set up.
	 * @since 1.0.3
	 *
	 * @example
	 * // Play a video and handle the stop event
	 * await sos.video.play('https://example.com/video.mp4', 0, 0, 1920, 1080);
	 * sos.video.onStop((event) => {
	 *     console.log('Video stopped:', event);
	 * });
	 * // Stop the video later...
	 * await sos.video.stop('https://example.com/video.mp4', 0, 0, 1920, 1080);
	 */
	@debug(DEBUG_NAMESPACE)
	public onStop(listener: (event: IVideoEvent) => void): void {
		validate({ listener }).required().function();
		this.eventEmitter.addListener('stop', listener);
	}

	/**
	 * The `onPause()` method sets up a listener, which is called whenever a video is paused with `pause()` method.
	 *
	 * @param listener The listener function to be called when a pause event occurs.
	 * @returns {void} Resolves when the listener is successfully set up.
	 * @since 1.0.3
	 *
	 * @example
	 * // Play a video and handle the pause event
	 * await sos.video.play('https://example.com/video.mp4', 0, 0, 1920, 1080);
	 * sos.video.onPause((event) => {
	 *     console.log('Video paused:', event); // Logs the pause event
	 * });
	 */
	@debug(DEBUG_NAMESPACE)
	public onPause(listener: (event: IVideoEvent) => void): void {
		validate({ listener }).required().function();
		this.eventEmitter.addListener('pause', listener);
	}

	/**
	 * The `onResume()` method sets up a listener, which is called whenever a video is resumed with the `resume()` method.
	 *
	 * @param listener The listener function to be called when a resume event occurs.
	 * @returns {void} Resolves when the listener is successfully set up.
	 * @since 1.0.3
	 *
	 * @example
	 * // Play a video and handle the resume event
	 * await sos.video.play('https://example.com/video.mp4', 0, 0, 1920, 1080);
	 * sos.video.onResume((event) => {
	 *     console.log('Video resumed:', event); // Logs the resume event
	 * });
	 * await sos.video.pause('https://example.com/video.mp4', 0, 0, 1920, 1080);
	 * await sos.video.resume('https://example.com/video.mp4', 0, 0, 1920, 1080);
	 */
	@debug(DEBUG_NAMESPACE)
	public onResume(listener: (event: IVideoEvent) => void): void {
		validate({ listener }).required().function();

		this.eventEmitter.addListener('resume', listener);
	}

	/**
	 * The `onEnded()` method sets up a listener, which is called whenever a video finished playing successfully.
	 *
	 * @param listener The listener function to be called when an ended event occurs.
	 * @returns {void} Resolves when the listener is successfully set up.
	 * @since 1.0.3
	 *
	 * @example
	 * // Play a video and handle the end of playback
	 * await sos.video.play('https://example.com/video.mp4', 0, 0, 1920, 1080);
	 * sos.video.onEnded((event) => {
	 *     console.log('Video ended:', event);
	 * });
	 */
	@debug(DEBUG_NAMESPACE)
	public onEnded(listener: (event: IVideoEvent) => void): void {
		validate({ listener }).required().function();
		this.eventEmitter.addListener('ended', listener);
	}

	/**
	 * The `onError()` method sets up a listener, which is called whenever a video failed to finish playing.
	 *
	 * @param listener The listener function to be called when an error occurs.
	 * @returns {void} Resolves when the listener is successfully set up.
	 * @since 1.0.3
	 *
	 * @example
	 * // Play a video and handle errors
	 * await sos.video.play('https://example.com/video.mp4', 0, 0, 1920, 1080);
	 * sos.video.onError((event) => {
	 *     console.error('Video error:', event);
	 * });
	 */
	@debug(DEBUG_NAMESPACE)
	public onError(listener: (event: IVideoEvent) => void): void {
		validate({ listener }).required().function();
		this.eventEmitter.addListener('error', listener);
	}

	/** @internal */
	public handleMessageData(data: IVideoMessage) {
		switch (data.type) {
			case this.getMessage('ended'):
			case this.getMessage('error'): {
				const type = data.type.substring(this.getMessage('').length) as 'ended' | 'error';
				this.createAndEmitEvent(type, data.uri, data.x, data.y, data.width, data.height);
				break;
			}
			default:
		}
	}

	/**
	 * The `removeEventListeners()` method removes all listeners set up for all videos.
	 */
	@debug(DEBUG_NAMESPACE)
	public removeEventListeners() {
		this.eventEmitter.removeAllListeners();
	}

	private createOncePromise(listenType: VideoEventType, uri: string, x: number, y: number, width: number, height: number) {
		const deferred: IDeferred = {} as IDeferred;
		deferred.oncePromise = new Promise<void>((resolve: () => void, reject: (error: Error) => void) => {
			deferred.reject = reject;

			const srcArguments = { uri, x, y, width, height };
			const listenEventName = getEventName({ type: listenType, srcArguments });
			const errorEventName = getEventName({ type: 'error', srcArguments });

			const listener = (event: IVideoEvent) => {
				if (JSON.stringify(event.srcArguments) === JSON.stringify({ uri, x, y, width, height })) {
					this.eventEmitter.removeListener(listenEventName, listener);
					this.eventEmitter.removeListener(errorEventName, listener);
					if (event.type === listenType) {
						resolve();
					} else if (event.type === 'error') {
						reject(
							new SosError({
								kind: 'sosError',
								message: JSON.stringify(event.srcArguments),
								code: ErrorCodes.UNEXPECTED_EXCEPTION,
							}),
						);
					}
				}
			};
			this.eventEmitter.addListener(listenEventName, listener);
			this.eventEmitter.addListener(errorEventName, listener);
		});
		return deferred;
	}

	private getMessage(name: string) {
		return this.messagePrefix + '.' + Video.MESSAGE_PREFIX + '.' + name;
	}

	private removeAllVideoListeners(uri: string, x: number, y: number, width: number, height: number) {
		for (const type of VIDEO_EVENT_TYPES) {
			const eventName = getEventName({ type, srcArguments: { uri, x, y, width, height } });
			this.eventEmitter.removeAllListeners(eventName);
		}
	}

	private createAndEmitEvent(type: VideoEventType, uri: string, x: number, y: number, width: number, height: number) {
		const videoEvent: IVideoEvent = {
			type,
			srcArguments: {
				uri,
				x,
				y,
				width,
				height,
			},
		};
		const eventName = getEventName(videoEvent);
		this.eventEmitter.emit(eventName, videoEvent);
		this.eventEmitter.emit(videoEvent.type, videoEvent);
	}

	private checkParamsValidity(uri: string, x: number, y: number, width: number, height: number) {
		validate({ uri }).required().uri();
		validate({ x }).required().number();
		validate({ y }).required().number();
		validate({ width }).required().number();
		validate({ height }).required().number();
	}
}
